import React, { useState } from 'react';
import ErrorBoundary from './components/ErrorBoundary';
import { CalculatorProvider } from './context/CalculatorContext';
import { Calculator, Target, TrendingUp, PieChart, BarChart3, DollarSign, Calendar, Home, Building2, Users, Zap } from 'lucide-react';
import { Globe } from 'lucide-react';

// Import all calculator components
import CalculatorForm from './components/CalculatorForm';
import TraditionalCalculator from './components/TraditionalCalculator';
import ReinvestmentCalculator from './components/ReinvestmentCalculator';
import LongTermCalculator from './components/LongTermCalculator';
import ComparativeAnalysis from './components/ComparativeAnalysis';
import DreamSimulator from './components/DreamSimulator';
import PaymentCalculatorMain from './components/paymentCalculator/PaymentCalculatorMain';
import LifestyleCalculator from './components/LifestyleCalculator';
import { AlertTriangle } from 'lucide-react';
import WhatIfScenarioMode from './components/WhatIfScenarioMode';

type CalculatorType = 
  | 'traditional' 
  | 'reinvestment' 
  | 'longterm' 
  | 'comparative' 
  | 'dreams' 
  | 'payment' 
  | 'lifestyle'
  | 'whatif';

interface CalculatorOption {
  id: CalculatorType;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  description: string;
}

const App: React.FC = () => {
  const [selectedCalculator, setSelectedCalculator] = useState<CalculatorType | null>(null);

  const calculatorTypes: CalculatorOption[] = [
    {
      id: 'traditional',
      title: 'Certificado Cosecha Fija',
      subtitle: 'Análisis básico sin reinversión',
      icon: <Calculator className="w-8 h-8" />,
      color: 'from-blue-500 to-blue-600',
      description: 'Calcula el rendimiento básico de tu inversión en certificados'
    },
    {
      id: 'reinvestment',
      title: 'Certificado de Crecimiento Exponencial',
      subtitle: 'Interés Compuesto Multiplicador™ - Multiplica activos reales',
      icon: <TrendingUp className="w-8 h-8" />,
      color: 'from-green-500 to-green-600',
      description: 'Experimenta el Interés Compuesto Multiplicador™ que multiplica activos productivos'
    },
    {
      id: 'longterm',
      title: 'Certificado Sin Enganche – Acceso Inmediato',
      subtitle: 'Inversión con mayor horizonte y beneficios especiales',
      icon: <BarChart3 className="w-8 h-8" />,
      color: 'from-purple-500 to-purple-600',
      description: 'Proyecciones detalladas para inversiones de largo plazo'
    },
    {
      id: 'comparative',
      title: 'Certificado Frente al Mercado Tradicional',
      subtitle: 'Multiplicador™ vs Interés Compuesto Tradicional',
      icon: <PieChart className="w-8 h-8" />,
      color: 'from-teal-500 to-teal-600',
      description: 'Descubre la superioridad del Interés Compuesto Multiplicador™'
    },
    {
      id: 'dreams',
      title: 'Certificado Cosecha de Vida',
      subtitle: 'Multiplica tus sueños con activos reales',
      icon: <Zap className="w-8 h-8" />,
      color: 'from-pink-500 to-pink-600',
      description: 'Usa el Multiplicador™ para hacer realidad tus sueños más ambiciosos'
    },
    {
      id: 'payment',
      title: 'Certificado de Pagos',
      subtitle: 'Planifica tus pagos',
      icon: <DollarSign className="w-8 h-8" />,
      color: 'from-yellow-500 to-yellow-600',
      description: 'Simula diferentes esquemas de pago y financiamiento'
    },
    {
      id: 'lifestyle',
      title: 'Certificado para tu Estilo de Vida',
      subtitle: 'Vive donde quieras',
      icon: <Globe className="w-8 h-8" />,
      color: 'from-teal-500 to-teal-600',
      description: 'Descubre qué estilo de vida podrías mantener en diferentes ciudades del mundo'
    },
    {
      id: 'whatif',
      title: 'Certificado de Resiliencia Financiera',
      subtitle: 'Escenarios extremos y análisis de resiliencia',
      icon: <AlertTriangle className="w-8 h-8" />,
      color: 'from-red-500 to-red-600',
      description: 'Experimenta con escenarios extremos para evaluar la resiliencia de tu inversión'
    }
  ];

  const renderCalculator = () => {
    switch (selectedCalculator) {
      case 'traditional':
        return <TraditionalCalculator />;
      case 'reinvestment':
        return <ReinvestmentCalculator />;
      case 'longterm':
        return <LongTermCalculator />;
      case 'comparative':
        return <ComparativeAnalysis />;
      case 'dreams':
        return <DreamSimulator />;
      case 'payment':
        return <PaymentCalculatorMain />;
      case 'lifestyle':
        return <LifestyleCalculator onBack={() => setSelectedCalculator(null)} />;
      case 'whatif':
        return <WhatIfScenarioMode onBack={() => setSelectedCalculator(null)} />;
      default:
        return <CalculatorForm />;
    }
  };

  if (selectedCalculator) {
    return (
      <CalculatorProvider>
        <div className="min-h-screen bg-gray-50">
          <div className="bg-white shadow-sm border-b">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-16">
                <button
                  onClick={() => setSelectedCalculator(null)}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Home className="w-5 h-5" />
                  <span>Volver al menú principal</span>
                </button>
                <h1 className="text-xl font-semibold text-gray-800">
                  {calculatorTypes.find(calc => calc.id === selectedCalculator)?.title}
                </h1>
              </div>
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {renderCalculator()}
          </div>
        </div>
      </CalculatorProvider>
    );
  }

  return (
    <ErrorBoundary>
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Calculadoras de Inversión
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Elige la calculadora que mejor se adapte a tus necesidades de inversión y planificación financiera
          </p>
        </div>

        {/* Calculator Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {calculatorTypes.map((calculator) => (
            <div
              key={calculator.id}
              onClick={() => setSelectedCalculator(calculator.id)}
              className="group cursor-pointer bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
            >
              <div className={`h-32 bg-gradient-to-r ${calculator.color} flex items-center justify-center text-white`}>
                {calculator.icon}
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                  {calculator.title}
                </h3>
                <p className="text-sm text-gray-500 mb-3">
                  {calculator.subtitle}
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {calculator.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              ¿Por qué usar nuestras calculadoras?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-left">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Calculator className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1">Precisión</h3>
                  <p className="text-sm text-gray-600">Cálculos exactos basados en datos reales del mercado</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1">Proyecciones</h3>
                  <p className="text-sm text-gray-600">Visualiza el crecimiento de tu inversión a largo plazo</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Target className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1">Personalización</h3>
                  <p className="text-sm text-gray-600">Adapta los cálculos a tus metas específicas</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1">Comparación</h3>
                  <p className="text-sm text-gray-600">Compara con otras opciones de inversión</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </ErrorBoundary>
  );
};

export default App;